import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Trophy, X, Check, RotateCcw, Sparkles, Brain, Award, ChevronRight, Home } from 'lucide-react';

// Confetti function (optional, graceful fallback)
const triggerConfetti = () => {
  try {
    import('canvas-confetti').then((confettiModule) => {
      const confetti = confettiModule.default;
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
    });
  } catch (e) {
    console.log('Confetti not available');
  }
};

// Quiz Questions basées sur la base de connaissances
const QUIZ_QUESTIONS = [
  {
    id: 1,
    category: "Lignes principales",
    question: "Quelle ligne de la main représente la vitalité et l'énergie, mais ne prédit PAS la durée de vie ?",
    options: [
      "La ligne de cœur",
      "La ligne de vie",
      "La ligne de tête",
      "La ligne du destin"
    ],
    correctAnswer: 1,
    explanation: "La ligne de vie contourne le mont de Vénus et représente l'énergie vitale, la vitalité et la santé. Contrairement aux idées reçues, elle n'indique pas la durée de vie."
  },
  {
    id: 2,
    category: "Lignes principales",
    question: "Où se situe la ligne de cœur sur la paume ?",
    options: [
      "Au centre de la paume, verticalement",
      "En haut de la paume, sous les doigts",
      "Autour du pouce",
      "Sur le côté de la main"
    ],
    correctAnswer: 1,
    explanation: "La ligne de cœur est horizontale, située en haut de la paume, allant de sous l'auriculaire vers l'index ou le majeur."
  },
  {
    id: 3,
    category: "Monts",
    question: "Quel mont est associé à l'ambition, au leadership et à l'autorité ?",
    options: [
      "Le mont de Vénus",
      "Le mont de la Lune",
      "Le mont de Jupiter",
      "Le mont de Saturne"
    ],
    correctAnswer: 2,
    explanation: "Le mont de Jupiter, situé à la base de l'index, représente l'ambition, l'autorité, le leadership et le pouvoir."
  },
  {
    id: 4,
    category: "Interprétation",
    question: "Dans la chiromancie, quelle main représente le potentiel inné et l'hérédité ?",
    options: [
      "La main droite",
      "La main gauche",
      "Les deux mains également",
      "Cela dépend si on est droitier ou gaucher"
    ],
    correctAnswer: 1,
    explanation: "La main gauche représente le potentiel inné et l'hérédité, tandis que la main droite montre l'évolution, les choix personnels et la vie active."
  },
  {
    id: 5,
    category: "Lignes principales",
    question: "Une ligne de tête droite et marquée indique généralement :",
    options: [
      "Une personnalité introvertie et rêveuse",
      "Un esprit logique, cartésien et pratique",
      "Des difficultés de concentration",
      "Une grande émotivité"
    ],
    correctAnswer: 1,
    explanation: "Une ligne de tête droite et marquée indique un esprit clair, cartésien et logique, adapté aux sciences et aux structures."
  },
  {
    id: 6,
    category: "Monts",
    question: "Le mont de Vénus, situé à la base du pouce, est associé à :",
    options: [
      "L'intelligence et la logique",
      "La sensualité, l'amour et les émotions",
      "La carrière et le succès",
      "Les voyages et l'aventure"
    ],
    correctAnswer: 1,
    explanation: "Le mont de Vénus représente la sensualité, l'amour, les émotions, la beauté et les plaisirs des sens."
  },
  {
    id: 7,
    category: "Signes spéciaux",
    question: "Que signifie généralement une étoile sur la main ?",
    options: [
      "Malchance et obstacles",
      "Un événement inévitable, rapide et intense",
      "Une période de calme",
      "Un voyage prochain"
    ],
    correctAnswer: 1,
    explanation: "Une étoile symbolise un événement inévitable, rapide et intense. Sa signification varie selon sa position sur la main."
  },
  {
    id: 8,
    category: "Observations anatomiques",
    question: "Des lunules (demi-lunes blanches) très visibles à la base des ongles indiquent généralement :",
    options: [
      "Une mauvaise santé",
      "Une bonne circulation et vitalité",
      "Un stress important",
      "Des carences nutritionnelles"
    ],
    correctAnswer: 1,
    explanation: "Des lunules bien visibles sont un signe positif indiquant une bonne circulation sanguine et une vitalité optimale."
  },
  {
    id: 9,
    category: "Types de mains",
    question: "Une main avec une paume carrée et des doigts longs est associée à quel élément ?",
    options: [
      "Terre - nature pratique",
      "Feu - nature passionnée",
      "Eau - nature émotionnelle et intuitive",
      "Air - nature intellectuelle"
    ],
    correctAnswer: 2,
    explanation: "Une main carrée avec des doigts longs correspond à l'élément Eau, indiquant une nature émotionnelle, intuitive et créative."
  },
  {
    id: 10,
    category: "Histoire",
    question: "Quel personnage célèbre a contribué à la vulgarisation internationale de la chiromancie au début du 20ème siècle ?",
    options: [
      "Adolphe Desbarrolles",
      "Aristote",
      "Cheiro (William John Warner)",
      "Hippocrate"
    ],
    correctAnswer: 2,
    explanation: "Cheiro (1866-1936), de son vrai nom William John Warner, était un chiromancien irlandais qui a popularisé la pratique à l'échelle internationale."
  },
  {
    id: 11,
    category: "Lignes secondaires",
    question: "L'absence de ligne de santé sur la main indique généralement :",
    options: [
      "Une mauvaise santé",
      "Souvent un signe d'excellente santé naturelle",
      "Des problèmes digestifs",
      "Une courte durée de vie"
    ],
    correctAnswer: 1,
    explanation: "Paradoxalement, l'absence de ligne de santé est souvent interprétée comme un signe d'excellente santé naturelle."
  },
  {
    id: 12,
    category: "Monts",
    question: "Le mont de la Lune est associé à :",
    options: [
      "L'ambition et le pouvoir",
      "L'amour et la sensualité",
      "L'imagination, l'intuition et les rêves",
      "La communication et le commerce"
    ],
    correctAnswer: 2,
    explanation: "Le mont de la Lune, situé du côté opposé au pouce, représente l'imagination, l'intuition, les rêves et la créativité."
  },
  {
    id: 13,
    category: "Interprétation",
    question: "Une ligne de cœur qui commence sous l'index suggère :",
    options: [
      "Une vision individualiste de l'amour",
      "Une vie émotionnelle satisfaisante mais exigeante",
      "Une tendance à tomber amoureux facilement",
      "Un détachement émotionnel"
    ],
    correctAnswer: 1,
    explanation: "Une ligne de cœur débutant sous l'index indique une vie émotionnelle satisfaisante mais avec des standards élevés en amour."
  },
  {
    id: 14,
    category: "Signes spéciaux",
    question: "Un triangle sur la main symbolise généralement :",
    options: [
      "Des obstacles et difficultés",
      "Des capacités spéciales et talents",
      "Une période de confusion",
      "Des problèmes de santé"
    ],
    correctAnswer: 1,
    explanation: "Un triangle représente des capacités spéciales, des talents et le succès obtenu par l'intelligence."
  },
  {
    id: 15,
    category: "Formes de doigts",
    question: "Des doigts carrés indiquent une personnalité :",
    options: [
      "Intuitive et rêveuse",
      "Logique, pratique et rigoureuse",
      "Rebelle et originale",
      "Émotionnelle et sensible"
    ],
    correctAnswer: 1,
    explanation: "Les doigts carrés sont associés à la logique, au sens pratique, à l'ordre, à la rigueur et à la fiabilité."
  },
  {
    id: 16,
    category: "Lignes principales",
    question: "La ligne du destin, lorsqu'elle est absente, signifie :",
    options: [
      "Une mort précoce",
      "Un échec professionnel assuré",
      "Une liberté de choix totale et besoin de remise en question",
      "L'absence de personnalité"
    ],
    correctAnswer: 2,
    explanation: "L'absence de ligne du destin indique une liberté totale dans les choix de vie, avec la possibilité de créer consciemment son propre chemin."
  },
  {
    id: 17,
    category: "Histoire",
    question: "Dans quelle civilisation trouve-t-on les premières traces de chiromancie (vers 3000 av. J.-C.) ?",
    options: [
      "Égypte et Rome",
      "Inde et Chine",
      "Grèce et Perse",
      "Mésopotamie et Assyrie"
    ],
    correctAnswer: 1,
    explanation: "Les premières traces de chiromancie remontent à environ 3000 av. J.-C. dans les textes védiques indiens et la tradition chinoise."
  },
  {
    id: 18,
    category: "Observations anatomiques",
    question: "Des stries horizontales sur les ongles (lignes de Beau) peuvent indiquer :",
    options: [
      "Un vieillissement naturel",
      "Un stress physiologique important vécu récemment",
      "Une bonne santé",
      "Des talents artistiques"
    ],
    correctAnswer: 1,
    explanation: "Les lignes de Beau témoignent d'un stress physiologique important (maladie, choc émotionnel, changement majeur). Comme les cernes d'un arbre, elles datent les événements."
  },
  {
    id: 19,
    category: "Monts",
    question: "Quel mont est associé à la créativité, l'art et le succès ?",
    options: [
      "Le mont de Mars",
      "Le mont d'Apollon",
      "Le mont de Saturne",
      "Le mont de Mercure"
    ],
    correctAnswer: 1,
    explanation: "Le mont d'Apollon, situé à la base de l'annulaire, représente l'art, la créativité, la beauté, le succès et le rayonnement."
  },
  {
    id: 20,
    category: "Principes",
    question: "Quel est le principe fondamental de l'interprétation en chiromancie ?",
    options: [
      "Prédire précisément l'avenir",
      "Effrayer les gens pour qu'ils changent",
      "La chiromancie montre des tendances, pas des absolus",
      "Seule la main droite compte"
    ],
    correctAnswer: 2,
    explanation: "La chiromancie montre des tendances, pas des absolus. Le libre arbitre permet toujours de modifier son destin, et l'approche doit rester positive et constructive."
  }
];

const QuizPage = () => {
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);

  const question = QUIZ_QUESTIONS[currentQuestion];
  const progress = ((currentQuestion + 1) / QUIZ_QUESTIONS.length) * 100;

  const handleAnswerSelect = (index) => {
    if (showExplanation) return;
    setSelectedAnswer(index);
  };

  const handleValidate = () => {
    if (selectedAnswer === null) return;

    const isCorrect = selectedAnswer === question.correctAnswer;
    
    setAnswers([...answers, {
      questionId: question.id,
      selected: selectedAnswer,
      correct: question.correctAnswer,
      isCorrect
    }]);

    if (isCorrect) {
      setScore(score + 1);
    }

    setShowExplanation(true);
  };

  const handleNext = () => {
    if (currentQuestion < QUIZ_QUESTIONS.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    } else {
      setQuizCompleted(true);
      // Trigger confetti for good scores
      if (score >= QUIZ_QUESTIONS.length * 0.7) {
        triggerConfetti();
      }
    }
  };

  const restartQuiz = () => {
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setScore(0);
    setAnswers([]);
    setQuizCompleted(false);
    setShowExplanation(false);
  };

  const getScoreMessage = () => {
    const percentage = (score / QUIZ_QUESTIONS.length) * 100;
    if (percentage >= 90) return { title: "Excellent !", message: "Vous êtes un expert en chiromancie !", emoji: "🏆" };
    if (percentage >= 70) return { title: "Très bien !", message: "Vous avez de solides connaissances !", emoji: "🌟" };
    if (percentage >= 50) return { title: "Bien !", message: "Continuez à apprendre pour vous améliorer.", emoji: "👍" };
    return { title: "Courage !", message: "Consultez la base de connaissances pour progresser.", emoji: "📚" };
  };

  const getCategoryColor = (category) => {
    const colors = {
      "Lignes principales": "bg-emerald-500",
      "Monts": "bg-purple-500",
      "Signes spéciaux": "bg-amber-500",
      "Observations anatomiques": "bg-rose-500",
      "Types de mains": "bg-blue-500",
      "Histoire": "bg-orange-500",
      "Interprétation": "bg-teal-500",
      "Formes de doigts": "bg-indigo-500",
      "Lignes secondaires": "bg-cyan-500",
      "Principes": "bg-lime-600"
    };
    return colors[category] || "bg-gray-500";
  };

  if (quizCompleted) {
    const scoreInfo = getScoreMessage();
    
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20 py-12 px-4 md:px-8">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card rounded-3xl border border-border shadow-lg p-8 text-center"
          >
            <div className="text-6xl mb-6">{scoreInfo.emoji}</div>
            <h1 className="font-heading text-3xl md:text-4xl mb-4">{scoreInfo.title}</h1>
            <p className="text-muted-foreground text-lg mb-8">{scoreInfo.message}</p>
            
            <div className="bg-secondary/30 rounded-2xl p-6 mb-8">
              <div className="flex items-center justify-center gap-4 mb-4">
                <Trophy className="h-8 w-8 text-primary" />
                <span className="text-4xl font-bold text-primary">{score}/{QUIZ_QUESTIONS.length}</span>
              </div>
              <p className="text-sm text-muted-foreground">
                {Math.round((score / QUIZ_QUESTIONS.length) * 100)}% de bonnes réponses
              </p>
            </div>

            {/* Detailed Results */}
            <div className="text-left mb-8">
              <h3 className="font-semibold mb-4">Détail par catégorie :</h3>
              <div className="space-y-2">
                {Object.entries(
                  answers.reduce((acc, answer) => {
                    const q = QUIZ_QUESTIONS.find(q => q.id === answer.questionId);
                    if (!acc[q.category]) acc[q.category] = { correct: 0, total: 0 };
                    acc[q.category].total++;
                    if (answer.isCorrect) acc[q.category].correct++;
                    return acc;
                  }, {})
                ).map(([category, stats]) => (
                  <div key={category} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <span className={`w-3 h-3 rounded-full ${getCategoryColor(category)}`}></span>
                      {category}
                    </span>
                    <span className={stats.correct === stats.total ? "text-green-600 font-medium" : "text-muted-foreground"}>
                      {stats.correct}/{stats.total}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={restartQuiz}
                className="flex-1 rounded-full bg-primary hover:bg-primary/90"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Recommencer
              </Button>
              <Button
                onClick={() => navigate('/knowledge')}
                variant="outline"
                className="flex-1 rounded-full"
              >
                <Brain className="h-4 w-4 mr-2" />
                Base de connaissances
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="ghost"
                className="flex-1 rounded-full"
              >
                <Home className="h-4 w-4 mr-2" />
                Accueil
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20 py-12 px-4 md:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button
            data-testid="back-button"
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-6 rounded-full hover:bg-secondary/50"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Button>
          
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Brain className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h1 className="font-heading text-2xl">Quiz Chiromancie</h1>
                <p className="text-sm text-muted-foreground">Testez vos connaissances</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-primary">{currentQuestion + 1}/{QUIZ_QUESTIONS.length}</p>
              <p className="text-xs text-muted-foreground">Score: {score}</p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="h-2 bg-secondary/30 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-primary rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </motion.div>

        {/* Question Card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="bg-card rounded-3xl border border-border shadow-lg overflow-hidden"
          >
            {/* Category Badge */}
            <div className={`px-6 py-3 ${getCategoryColor(question.category)}`}>
              <span className="text-white text-sm font-medium">{question.category}</span>
            </div>

            <div className="p-6 md:p-8">
              {/* Question */}
              <h2 className="font-heading text-xl md:text-2xl mb-8 leading-relaxed">
                {question.question}
              </h2>

              {/* Options */}
              <div className="space-y-3 mb-8">
                {question.options.map((option, index) => {
                  const isSelected = selectedAnswer === index;
                  const isCorrect = index === question.correctAnswer;
                  const showCorrectness = showExplanation;

                  let bgClass = "bg-secondary/20 hover:bg-secondary/40 border-transparent";
                  let iconClass = null;

                  if (showCorrectness) {
                    if (isCorrect) {
                      bgClass = "bg-green-100 border-green-500";
                      iconClass = <Check className="h-5 w-5 text-green-600" />;
                    } else if (isSelected && !isCorrect) {
                      bgClass = "bg-red-100 border-red-500";
                      iconClass = <X className="h-5 w-5 text-red-600" />;
                    }
                  } else if (isSelected) {
                    bgClass = "bg-primary/20 border-primary";
                  }

                  return (
                    <button
                      key={index}
                      onClick={() => handleAnswerSelect(index)}
                      disabled={showExplanation}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center justify-between ${bgClass} ${
                        showExplanation ? 'cursor-default' : 'cursor-pointer'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          isSelected && !showCorrectness ? 'bg-primary text-white' : 'bg-secondary/50'
                        }`}>
                          {String.fromCharCode(65 + index)}
                        </span>
                        <span className="text-sm md:text-base">{option}</span>
                      </div>
                      {iconClass}
                    </button>
                  );
                })}
              </div>

              {/* Explanation */}
              <AnimatePresence>
                {showExplanation && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mb-6"
                  >
                    <div className={`p-4 rounded-xl ${
                      selectedAnswer === question.correctAnswer 
                        ? 'bg-green-50 border border-green-200' 
                        : 'bg-amber-50 border border-amber-200'
                    }`}>
                      <div className="flex items-start gap-3">
                        <Sparkles className={`h-5 w-5 mt-0.5 ${
                          selectedAnswer === question.correctAnswer 
                            ? 'text-green-600' 
                            : 'text-amber-600'
                        }`} />
                        <div>
                          <p className={`font-medium mb-1 ${
                            selectedAnswer === question.correctAnswer 
                              ? 'text-green-800' 
                              : 'text-amber-800'
                          }`}>
                            {selectedAnswer === question.correctAnswer ? 'Correct !' : 'Explication'}
                          </p>
                          <p className={`text-sm ${
                            selectedAnswer === question.correctAnswer 
                              ? 'text-green-700' 
                              : 'text-amber-700'
                          }`}>
                            {question.explanation}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Actions */}
              <div className="flex justify-end">
                {!showExplanation ? (
                  <Button
                    onClick={handleValidate}
                    disabled={selectedAnswer === null}
                    className="rounded-full bg-primary hover:bg-primary/90 px-8"
                  >
                    Valider
                  </Button>
                ) : (
                  <Button
                    onClick={handleNext}
                    className="rounded-full bg-primary hover:bg-primary/90 px-8"
                  >
                    {currentQuestion < QUIZ_QUESTIONS.length - 1 ? (
                      <>
                        Question suivante
                        <ChevronRight className="h-4 w-4 ml-2" />
                      </>
                    ) : (
                      <>
                        Voir mes résultats
                        <Trophy className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default QuizPage;
